import * as signalr from "@microsoft/signalr";
import OutboundEventMap from "./events/outboundEventMap";
import InboundEventMap from "./events/inboundEventMap";
import HubConnection from "./hubConnection";
import HubConnectionOptions from "./hubConnectionOptions";
import "core-js/stable";

declare const window: any;

/**
 * A builder for configuring {@link @omnius/signalr/authentication/hubConnection} instances.
 * Connection options for this class can be configured by via a {@link @omnius/signalr/authentication/hubConnectionOptions}
 * object set to a property named "__authenticationHubConnectionOptions" on the document window or via the ctor.
 */
export default class HubConnectionBuilder {
  private readonly hubConnectionOptions: HubConnectionOptions;

  /**
   * Creates a new builder from which new hub connections can be made.
   * @param connectionOptions [Optional] The connection options to use when building a new connection to the hub.
   * If provided, overrides any/all options set via the "__authenticationHubConnectionOptions" global config
   */
  constructor(connectionOptions?: HubConnectionOptions) {
    this.hubConnectionOptions = window.__authenticationHubConnectionOptions && (window.__authenticationHubConnectionOptions as HubConnectionOptions).url
      ? window.__authenticationHubConnectionOptions
      : connectionOptions;
  }

  /**
   * Creates and returns a new connection to the hub
   */
  public getConnection(): HubConnection {
    const builder = new signalr.HubConnectionBuilder()
      .withUrl(this.hubConnectionOptions.url)
      .withAutomaticReconnect()
      .configureLogging(signalr.LogLevel.Information);

    return new AuthenticationHubConnection(builder.build());
  }
}

class AuthenticationHubConnection implements HubConnection {
  private connection: signalr.HubConnection;

  constructor(hubConnection: signalr.HubConnection) {
    this.connection = hubConnection;
    this.initializeConnection();
  }

  /**
   * Registers a listener that will be invoked when the event, of the specified type, fires.
   * @param type The type of hub event to subscribe to
   * @param listener The listener to be invoked
   */
  public subscribe<K extends keyof InboundEventMap>(type: K, listener: (ev: InboundEventMap[K]) => any): HubConnection {
    console.debug("AuthenticationHubConnection: Subscribing to event: ", type);
    this.connection.on(type, listener);
    return this;
  }

  /**
   * Removes the specified listener for the event of the specified type.
   * You must pass the exact same Function instance as was previously passed to "subscribe". Passing a different instance (even if the function
   * body is the same) will not remove the listener.
   * @param type The type of event to remove the listener for
   * @param listener The listener to remove
   */
  public unsubscribe<K extends keyof InboundEventMap>(type: K, listener: (ev: InboundEventMap[K]) => any): HubConnection {
    console.debug("AuthenticationHubConnection: Unsubscribing to event: ", type);
    this.connection.off(type, listener);
    return this;
  }

  public async invoke<K extends keyof OutboundEventMap>(type: K, args: OutboundEventMap[K]): Promise<HubConnection> {
    console.debug("AuthenticationHubConnection: Informing server of client event: ", type);
    await this.connection.invoke(type, args);
    return this;
  }

  private initializeConnection(): void {
    //todo retry stuff and maybe move out of here
    console.debug("AuthenticationHubConnection: Starting hub connection");
    this.connection.start();
  }
}